import { onValue, ref } from 'firebase/database';
import config from '../config';
import { database } from '../firebase';

/**
 * Single owner of the gateway-selection logic.
 *
 * **The invariant is per consumer, not global:** a token must be minted at the
 * gateway it will be *presented to*. An access token minted by api-edge's token
 * service is an opaque UUID in api-edge's own store, and Apigee cannot resolve
 * it — presenting one to the other gateway fails with `401 "api key is
 * invalid"`, which is Apigee's message for an unresolvable token and not a
 * consumer-key problem at all. The mirror image is a real prod incident: on
 * 2026-09-04 cs-widgets was pointed at api-edge while this host still minted at
 * Apigee, and api-edge's authorizer returned 403 for every credentialed widget
 * operation.
 *
 * Two shapes of consumer live here, and the difference is the whole design:
 *
 * - **Non-React singletons** — `apolloClient` and `authServiceFactory` have no
 *   context to read, so `resolveGraphqlUri` and `resolveApigeeAuthUrl` read the
 *   module-level flag below. Safe only because both are evaluated per request,
 *   long after Firebase has answered.
 * - **React consumers** — everything else takes the flag as an argument and the
 *   caller reads it from `featureFlagsContext`. This is not a style preference.
 *   A module-level flag initialises to `false` and only becomes true when
 *   `onValue` fires, and it emits no React notification, so a component reading
 *   it computes a value that never updates. That bug shipped twice: once in
 *   `WidgetHost`'s mount effect (fixed in PR #5825), and once in
 *   `useViewAndDownloadBills`, where the *request URL* was frozen at render while
 *   the *token* was chosen at click — measured in prod at 03:29Z on 2026-09-05 as
 *   a 403 with `useEdgeBillApiToken` already `true` in Firebase.
 *
 * **So: if a React component needs a gateway decision, pass the flag in.**
 */
let useLegacyApigeeEndpoint = false;

/* This listener runs at module scope, and `apolloClient` and `authServiceFactory`
 * both import this module — so a throw here takes out the Apollo client and the
 * auth service together. The `database` guard is what prevents that throw
 * (`ref(undefined)` throws). The error callback only replaces Firebase's unhandled
 * warning with a clear log line; after it fires the listen is cancelled for the
 * session and the flag stays at the `false` default above, which selects the
 * current gateway — a failed read degrades instead of breaking the app. */
if (typeof window !== 'undefined' && database) {
  onValue(
    ref(database, '/featureFlags'),
    snapshot => {
      useLegacyApigeeEndpoint = !!snapshot.val()?.useLegacyApigeeEndpoint;
    },
    error => {
      console.error(
        `gatewayEndpoint: feature flag listener failed, staying on the current gateway: ${error.message}`,
      );
    },
  );
}

/** GraphQL endpoint. Both the BFF Core and PGEPlus links share it. */
export const resolveGraphqlUri = () =>
  (useLegacyApigeeEndpoint && config.graphql.baseUrlLegacy) ||
  config.graphql.baseUrl;

/** Host that mints the host session token via `/pg-token-implicit-aws/token`. */
export const resolveApigeeAuthUrl = () =>
  (useLegacyApigeeEndpoint && config.apigee.auth.urlLegacy) ||
  config.apigee.auth.url;

/**
 * The single source of truth for the `/pge-bill-api/*` gateway — used BOTH for the
 * request URL and as the mint base for the token that request carries.
 *
 * One function on purpose. The `billHistory` 403 existed because the URL was
 * computed from `config.edgeApi.*` inside the hooks while the token came from
 * `config.apigee.auth.*` via `authService` — two places deciding a host that must
 * agree. Takes the flag rather than reading it, so the caller resolves the URL and
 * the mint host from one context read at one instant; that is what the 03:29Z
 * regression above cost us.
 */
export const resolveBillApiUrl = (useLegacy: boolean): string =>
  (useLegacy && config.edgeApi.baseUrlLegacy) || config.edgeApi.baseUrl;

const originOf = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

/**
 * Which host should mint the token postMessaged to the cs-widgets iframe.
 *
 * `null` means "hand over the host session token", i.e. today's behaviour.
 * A string means "mint a dedicated widget token at this base".
 *
 * The widget presents its token to whatever gateway cs-widgets was *built*
 * against — a `VITE_API_URL` in another repo, which nothing here can see. So the
 * widget reports it in its `ready` message and we mint to match. That makes
 * "mint where you present" structural instead of two repos agreeing on a flag,
 * and it removes the release-ordering deadlock: the widget's lever is build-time
 * and this one is runtime, so under a flag alone neither repo could move first.
 *
 * `apiOrigin` arrives over postMessage, so it is untrusted input. It only ever
 * *selects* among hosts this app already knows; an unrecognised origin falls back
 * to the session token rather than minting somewhere arbitrary. Never mint at a
 * host that came off the wire.
 *
 * When the reported origin already matches where the session token was minted,
 * return `null` — the session token is the correct credential and a second mint
 * would buy nothing. That is the steady state both before the cutover (both
 * Apigee) and after it (both api-edge).
 */
export const resolveWidgetMintBase = (
  apiOrigin: string | null | undefined,
  useEdgeWidgetToken: boolean,
): string | null => {
  if (!apiOrigin) {
    // Old widget builds report nothing. Fall back to the rollout flag so this
    // stays a no-op until cs-widgets ships the handshake.
    return useEdgeWidgetToken ? config.edgeApi.baseUrl : null;
  }

  const reported = originOf(apiOrigin);
  if (!reported) {
    return null;
  }

  if (reported === originOf(resolveApigeeAuthUrl())) {
    return null;
  }

  const allowed = [
    config.edgeApi.baseUrl,
    config.edgeApi.baseUrlLegacy,
    config.apigee.auth.url,
    config.apigee.auth.urlLegacy,
  ];

  return allowed.find(base => originOf(base) === reported) ?? null;
};
